import { useCallback } from 'react';
import { useStore } from '../store/useStore';
import type { PuffLog, Stats, User } from '@puff/shared';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export function usePuffApi() {
  const { user, addPuffLog, removePuffLog, setUser, setOnboardingCompleted, setStats } = useStore();

  // Log a puff
  const logPuff = useCallback(async (count: number = 1, trigger?: string, notes?: string) => {
    if (!user) throw new Error('User not authenticated');

    const response = await fetch(`${API_BASE_URL}/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        count,
        trigger,
        notes,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to log puff');
    }

    const data = await response.json();
    addPuffLog(data.log as PuffLog);

    return data.log;
  }, [user, addPuffLog]);

  // Delete a log
  const deleteLog = useCallback(async (logId: string) => {
    const response = await fetch(`${API_BASE_URL}/log/${logId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete log');
    }

    removePuffLog(logId);
  }, [removePuffLog]);

  // Complete onboarding
  const completeOnboarding = useCallback(async (settings: {
    mode: 'quit' | 'reduce';
    dailyPuffLimit: number;
    quitDate?: Date;
    costPerPuff: number;
    nicotinePerPuff: number;
    puffsPerPod?: number;
    podPrice?: number;
    smokingType?: string;
    smokingDuration?: string;
    dailyAmount?: number;
    quitReasons?: string[];
    monthlyCost?: number;
  }) => {
    if (!user) throw new Error('User not authenticated');

    const response = await fetch(`${API_BASE_URL}/user/onboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        ...settings,
        quitDate: settings.quitDate?.toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to complete onboarding');
    }

    setUser({ ...user, ...settings, onboardingCompleted: true } as User);
    setOnboardingCompleted(true);
  }, [user, setUser, setOnboardingCompleted]);

  // Update user settings
  const updateSettings = useCallback(async (settings: Partial<User>) => {
    if (!user) throw new Error('User not authenticated');

    // Note: This would need a corresponding API endpoint
    // For now, just update locally
    setUser({ ...user, ...settings } as User);
  }, [user, setUser]);

  // Refresh stats
  const refreshStats = useCallback(async (period: 'day' | 'week' | 'month' = 'week') => {
    if (!user) throw new Error('User not authenticated');

    const response = await fetch(`${API_BASE_URL}/stats/${user.id}?period=${period}`);

    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }

    const data = await response.json();
    setStats(data as Stats);

    return data as Stats;
  }, [user, setStats]);

  return {
    logPuff,
    deleteLog,
    completeOnboarding,
    updateSettings,
    refreshStats,
  };
}
